import { useState } from 'react';

const hasGiftLevel = (product) => {
  return product.giftLevel && Object.keys(product.giftLevel).some(key => 
    key !== 'currency' && product.giftLevel[key] > 0
  );
};

// Calculate the ADDITIONAL gift value the customer receives by upgrading
const calculateUpgradeGiftValue = (upperSetGiftLevel, lowerSetGiftLevel) => {
  if (!upperSetGiftLevel) return null;
  if (!lowerSetGiftLevel) return upperSetGiftLevel;
  
  const upperCurrency = upperSetGiftLevel.currency;
  const lowerCurrency = lowerSetGiftLevel.currency;
  
  // Convert lower set values to upper set's currency if needed
  let conversionRate = 1;
  
  if (upperCurrency !== lowerCurrency) {
    // BHD is the base (1 BHD = 10 AED = 10 SAR)
    if (upperCurrency === 'BHD' && (lowerCurrency === 'AED' || lowerCurrency === 'SAR')) {
      // Lower is AED/SAR, convert to BHD: divide by 10
      conversionRate = 0.1;
    } 
    else if ((upperCurrency === 'AED' || upperCurrency === 'SAR') && lowerCurrency === 'BHD') {
      // Lower is BHD, convert to AED/SAR: multiply by 10
      conversionRate = 10;
    }
    else {
      console.error('Unsupported currency mismatch in gift level upgrade:', {
        upperSetCurrency: upperCurrency,
        lowerSetCurrency: lowerCurrency
      });
      return upperSetGiftLevel;
    }
  }
  
  // Calculate the ADDITIONAL gift value the customer gets
  const additionalGiftValue = { currency: upperSetGiftLevel.currency };
  
  Object.keys(upperSetGiftLevel).forEach(key => {
    if (key !== 'currency') {
      const upperValue = upperSetGiftLevel[key] || 0;
      let lowerValue = lowerSetGiftLevel[key] || 0;
      
      // Convert lower value to upper currency if needed
      if (upperCurrency !== lowerCurrency) {
        lowerValue = lowerValue * conversionRate;
      }
      
      // This is the EXTRA gift value the customer receives by upgrading
      additionalGiftValue[key] = Math.max(0, upperValue - lowerValue);
    }
  });
  
  return additionalGiftValue;
};

export const useGiftSelection = (products, paidItems, cart, addGift, removeFromCart, setCart, upgradeInfo = null) => {
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [eligibleProducts, setEligibleProducts] = useState([]);
  const [selectedProductForGifts, setSelectedProductForGifts] = useState(null);

  // Check gift eligibility using local product attributes
  const checkGiftEligibility = () => {
    const eligible = paidItems.map(item => {
      // Cross-reference the original products array
      const fullProduct = products.find(p => p.id === item.id);
      if (!fullProduct) return null;
      
      let effectiveGiftLevel = fullProduct.giftLevel;
      
      // If this is an upgrade item with lower set SKU, look up the lower set's CURRENT gift levels
      if (item.isUpgrade && item.lowerSetSku && fullProduct.BrowsingGroup === 'SET') {
        const lowerSetProduct = products.find(p => p.sku === item.lowerSetSku);
        if (lowerSetProduct?.giftLevel) {
          effectiveGiftLevel = calculateUpgradeGiftValue(fullProduct.giftLevel, lowerSetProduct.giftLevel);
        }
      }
      // Also check global upgradeInfo as fallback
      else if (upgradeInfo?.eligible && upgradeInfo.lowerSetSku && fullProduct.BrowsingGroup === 'SET') {
        const lowerSetProduct = products.find(p => p.sku === upgradeInfo.lowerSetSku);
        if (lowerSetProduct?.giftLevel) {
          effectiveGiftLevel = calculateUpgradeGiftValue(fullProduct.giftLevel, lowerSetProduct.giftLevel);
        }
      }
      
      // Create a modified product with effective gift levels for this upgrade
      const productWithEffectiveGifts = {
        ...fullProduct,
        giftLevel: effectiveGiftLevel
      };
      
      if (hasGiftLevel(productWithEffectiveGifts)) {
        return productWithEffectiveGifts;
      }
      return null;
    }).filter(Boolean);
    
    setEligibleProducts(eligible);
  };

  // Open gift modal for a specific product
  const openGiftModalForProduct = (product) => {
    // Check if product is in eligible products
    const isEligible = eligibleProducts.some(p => p.id === product.id);
    
    if (!isEligible) {
      alert('This product does not have gift options available.');
      return;
    }
    
    setSelectedProductForGifts(product);
    setShowGiftModal(true);
  };

  const handleGiftSelectionComplete = (period, selectedGifts) => {
    // Add selected gift items to cart
    for (const [productId, giftSet] of Object.entries(selectedGifts)) {
      if (giftSet) {
        const giftItemIds = giftSet.split(' ');
        for (const itemId of giftItemIds) {
          const itemData = products.find(p => p.id === itemId);
          if (itemData && itemData.isActive !== false) {
            const giftItem = {
              ...itemData,
              id: itemId,
              name: itemData.name || `Gift Item ${itemId}`,
              price: 0,
              quantity: 1,
              isGift: true,
              imageUrl: itemData.imageUrl || '',
              category: itemData.category || 'Gift',
              parentProductId: productId,
              paymentPeriod: period
            };
            addGift(giftItem, 1);
          } else {
            console.warn(`Gift item ${itemId} not found in local products array.`);
          }
        }
      }
    }
    setShowGiftModal(false);
    setSelectedProductForGifts(null);
    alert('Gift items have been added to your cart!');
  };

  const closeGiftModal = () => {
    setShowGiftModal(false);
    setSelectedProductForGifts(null);
  };

  return {
    showGiftModal,
    eligibleProducts,
    selectedProductForGifts,
    checkGiftEligibility,
    handleGiftSelectionComplete,
    closeGiftModal,
    openGiftModalForProduct,
    setShowGiftModal
  };
};
